import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Building2, CheckCircle2, Headphones, Loader2, Mic, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { toastError } from '../../services/errorUtils'

interface OnboardingStatus {
  onboarding_completed: boolean
  onboarding_step: number
  organization_name: string
  provider_ready: boolean
  ai_ready: boolean
  provider_configured: boolean
  elevenlabs_configured: boolean
}

const STEPS = [
  { id: 1, label: 'Perfil', icon: Building2 },
  { id: 2, label: 'Operadora', icon: Headphones },
  { id: 3, label: 'Voz IA', icon: Mic },
]

export default function OnboardingWizard() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState<OnboardingStatus | null>(null)
  const [step, setStep] = useState(1)

  // Step 1 fields
  const [orgName, setOrgName] = useState('')
  const [fullName, setFullName] = useState('')

  // Step 2 fields
  const [wavoipToken, setWavoipToken] = useState('')
  const [sipHost, setSipHost] = useState('sipv2.wavoip.com')
  const [phoneNumber, setPhoneNumber] = useState('')

  // Step 3 fields
  const [elevenlabsKey, setElevenlabsKey] = useState('')

  useEffect(() => {
    apiService.getOnboardingStatus()
      .then((res) => {
        const s = res.data?.data
        setStatus(s)
        setStep(s?.onboarding_step || 1)
        setOrgName(s?.organization_name || '')
      })
      .catch((error) => toastError(error, 'Erro ao carregar onboarding'))
      .finally(() => setLoading(false))
  }, [])

  async function handleSaveProfile() {
    setSaving(true)
    try {
      await apiService.saveOnboardingProfile({ organization_name: orgName, full_name: fullName })
      setStep(2)
      toast.success('Perfil salvo!')
    } catch (error) {
      toastError(error, 'Erro ao salvar perfil')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveProvider() {
    setSaving(true)
    try {
      await apiService.saveOnboardingProvider({ wavoip_token: wavoipToken, sip_host: sipHost, phone_number: phoneNumber })
      setStep(3)
      toast.success('Operadora configurada!')
    } catch (error) {
      toastError(error, 'Erro ao configurar operadora')
    } finally {
      setSaving(false)
    }
  }

  async function handleSaveElevenlabs() {
    setSaving(true)
    try {
      await apiService.saveOnboardingElevenlabs({ elevenlabs_api_key: elevenlabsKey })
      await apiService.completeOnboarding()
      toast.success('Onboarding concluído!')
      navigate('/app')
    } catch (error) {
      toastError(error, 'Erro ao configurar voz IA')
    } finally {
      setSaving(false)
    }
  }

  function handleSkip() {
    navigate('/app')
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (status?.onboarding_completed) {
    navigate('/app', { replace: true })
    return null
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col justify-center px-4 py-8">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25">
          <Sparkles className="h-6 w-6" />
        </div>
        <h1 className="mt-4 text-2xl font-bold text-slate-900">Configure sua clínica</h1>
        <p className="mt-1 text-sm text-slate-500">
          Três passos para começar a usar o AiCall.
        </p>
      </div>

      {/* Step indicator */}
      <div className="mt-8 flex items-center justify-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
              step >= s.id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-400'
            }`}>
              {step > s.id ? <CheckCircle2 className="h-4 w-4" /> : s.id}
            </div>
            <span className={`text-sm font-medium ${step >= s.id ? 'text-slate-900' : 'text-slate-400'}`}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <div className={`h-px w-8 ${step > s.id ? 'bg-primary-400' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Perfil da organização</h2>
              <p className="mt-1 text-sm text-slate-500">Dados da sua clínica.</p>
            </div>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Nome da clínica</span>
              <input className="input mt-1.5" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Ex: Sorriso Dental" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Seu nome</span>
              <input className="input mt-1.5" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Como prefere ser chamado" />
            </label>
            <div className="flex gap-3 pt-2">
              <button type="button" className="btn-secondary flex-1" onClick={handleSkip}>Pular</button>
              <button type="button" className="btn-primary flex-1" disabled={!orgName || saving} onClick={handleSaveProfile}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continuar'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Operadora de voz</h2>
              <p className="mt-1 text-sm text-slate-500">Conecte sua conta Wavoip.</p>
            </div>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Token Wavoip</span>
              <input className="input mt-1.5 font-mono" value={wavoipToken} onChange={(e) => setWavoipToken(e.target.value)} placeholder="Seu token de integração" />
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">SIP Host</span>
                <input className="input mt-1.5 font-mono" value={sipHost} onChange={(e) => setSipHost(e.target.value)} />
              </label>
              <label className="block">
                <span className="text-sm font-semibold text-slate-700">Telefone</span>
                <input className="input mt-1.5 font-mono" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="5511999999999" />
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" className="btn-secondary flex-1" onClick={handleSkip}>Pular</button>
              <button type="button" className="btn-primary flex-1" disabled={saving} onClick={handleSaveProvider}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continuar'}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Voz com IA</h2>
              <p className="mt-1 text-sm text-slate-500">Configure sua chave ElevenLabs para vozes naturais.</p>
            </div>
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">ElevenLabs API Key</span>
              <input className="input mt-1.5 font-mono" value={elevenlabsKey} onChange={(e) => setElevenlabsKey(e.target.value)} placeholder="sk_..." />
            </label>
            <div className="flex gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </button>
              <button type="button" className="btn-secondary flex-1" onClick={handleSkip}>Pular</button>
              <button type="button" className="btn-primary flex-1" disabled={saving} onClick={handleSaveElevenlabs}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Concluir'}
                <CheckCircle2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
